import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, setSessionExpiredHandler, tokenStore } from '../api/client'
import type { AuthResponse, CurrentUser } from '../api/types'

interface AuthContextValue {
  user: CurrentUser | null
  loading: boolean
  login: (username: string, password: string) => Promise<CurrentUser>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /** Permission check in `MODULE:ACTION` form, matching the backend catalogue. */
  can: (permission: string) => boolean
  canAny: (...permissions: string[]) => boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  const clearSession = useCallback(() => {
    tokenStore.clear()
    setUser(null)
  }, [])

  useEffect(() => {
    setSessionExpiredHandler(clearSession)
  }, [clearSession])

  // Restore the session on a page refresh: the token survives, the user object does not.
  useEffect(() => {
    let cancelled = false
    async function restore() {
      if (!tokenStore.getAccess()) {
        setLoading(false)
        return
      }
      try {
        const { data } = await api.get<CurrentUser>('/auth/me')
        if (!cancelled) setUser(data)
      } catch {
        if (!cancelled) clearSession()
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [clearSession])

  const login = useCallback(async (username: string, password: string) => {
    const { data } = await api.post<AuthResponse>('/auth/login', {
      username,
      password,
      platform: 'WEB',
    })
    tokenStore.set(data.accessToken, data.refreshToken)
    setUser(data.user)
    return data.user
  }, [])

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefresh()
    try {
      await api.post('/auth/logout', refreshToken ? { refreshToken } : {})
    } catch {
      // Signing out locally must succeed even when the server cannot be reached.
    } finally {
      clearSession()
    }
  }, [clearSession])

  const refreshUser = useCallback(async () => {
    const { data } = await api.get<CurrentUser>('/auth/me')
    setUser(data)
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(user?.permissions ?? [])
    return {
      user,
      loading,
      login,
      logout,
      refreshUser,
      can: (permission: string) => permissions.has(permission),
      canAny: (...list: string[]) => list.some((p) => permissions.has(p)),
    }
  }, [user, loading, login, logout, refreshUser])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
